import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const postApiChat_Body = z.object({
  messages: z.array(
    z.object({
      role: z.string(),
      content: z.string().optional(),
      parts: z
        .array(
          z.union([
            z.string(),
            z.object({ type: z.string(), text: z.string().optional() }),
          ])
        )
        .optional(),
    })
  ),
  focusedId: z.string().optional(),
  interactible: z.string().optional(),
});
const postApiDataAgentsByAgentId_Body = z.object({
  chatId: z.string(),
  messages: z.array(
    z.object({
      role: z.string(),
      content: z.string().optional(),
      parts: z
        .array(
          z.union([
            z.string(),
            z.object({ type: z.string(), text: z.string().optional() }),
          ])
        )
        .optional(),
    })
  ),
  focusedId: z.string().optional(),
  interactible: z.string().optional(),
});

export const schemas = {
  postApiChat_Body,
  postApiDataAgentsByAgentId_Body,
};

const endpoints = makeApi([
  {
    method: "post",
    path: "/api/chat",
    alias: "postApiChat",
    description: `Chat with the AI agent`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: postApiChat_Body,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/data-agents",
    alias: "getApiDataAgents",
    description: `List all available data agents with optional pagination and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "page",
        type: "Query",
        schema: z
          .number()
          .int()
          .gte(1)
          .lte(9007199254740991)
          .optional()
          .default(1),
      },
      {
        name: "pageSize",
        type: "Query",
        schema: z.number().int().gte(1).lte(100).optional().default(10),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.object({
      agents: z.array(
        z.object({ id: z.string(), name: z.string(), description: z.string() })
      ),
      pagination: z.object({
        page: z.number(),
        pageSize: z.number(),
        total: z.number(),
        totalPages: z.number(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/data-agents/:agent_id",
    alias: "postApiDataAgentsByAgentId",
    description: `Chat with a specific data agent`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: postApiDataAgentsByAgentId_Body,
      },
      {
        name: "agent_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/data-agents/chats",
    alias: "getApiDataAgentsChats",
    description: `List saved chats`,
    requestFormat: "json",
    response: z.object({
      chats: z.array(
        z.object({
          id: z.string(),
          title: z.string(),
          createdAt: z.number(),
          updatedAt: z.number(),
          lastMessage: z.string().optional(),
          agentId: z.string().optional(),
        })
      ),
      cursor: z.string().optional(),
    }),
  },
  {
    method: "get",
    path: "/api/data-agents/history/:chatId",
    alias: "getApiDataAgentsHistoryByChatId",
    description: `Get chat history`,
    requestFormat: "json",
    parameters: [
      {
        name: "chatId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(
      z.object({
        role: z.string(),
        content: z.string(),
        timestamp: z.number(),
        metadata: z.object({}).partial().optional(),
      })
    ),
  },
  {
    method: "post",
    path: "/api/data-agents/route",
    alias: "postApiDataAgentsRoute",
    description: `Route a user message to the most appropriate data agent`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ message: z.string() }),
      },
    ],
    response: z.object({
      agentId: z.string(),
      agentName: z.string(),
      reasoning: z.string().optional(),
    }),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
